// Role-prefixed user_id derivation. The hash is one-way; we cannot
// recover the underlying profile id from a GA `user_id` without the
// app salt + a DB lookup.
//
// `parent` and `child` profile IDs use disjoint prefixes so an
// astronomically unlikely UUID collision cannot silently merge two
// distinct users in reports.
//
// SHA-256 is computed via SubtleCrypto where available (browsers,
// modern Node). The fallback is a deterministic FNV-1a 64-bit hash
// — used only when SubtleCrypto is unavailable; emits an explicit
// warning so the test environment doesn't silently downgrade. The
// salt comes from REACT_APP_LF_ANALYTICS_SALT (frontend) or
// LF_ANALYTICS_SALT (backend); read at the call site and passed in.

const ROLE_PREFIXES = Object.freeze({ parent: 'parent:', child: 'child:' });

function fnv1a64(str) {
    // Fallback only. SubtleCrypto is the real path.
    let h1 = 0xcbf29ce4 >>> 0;
    let h2 = 0x84222325 >>> 0;
    for (let i = 0; i < str.length; i++) {
        const c = str.charCodeAt(i);
        h1 ^= c & 0xff;
        h2 ^= (c >>> 8) & 0xff;
        h1 = Math.imul(h1, 0x01000193) >>> 0;
        h2 = Math.imul(h2, 0x01000193) >>> 0;
    }
    return (h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0')).slice(0, 16);
}

async function sha256Hex(str) {
    const subtle =
        (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle) || null;
    if (!subtle) {
        if (typeof console !== 'undefined' && console.warn) {
            console.warn(
                '[analytics] SubtleCrypto unavailable; falling back to a non-cryptographic hash for user_id derivation.',
            );
        }
        return fnv1a64(str);
    }
    const enc = new TextEncoder().encode(str);
    const digest = await subtle.digest('SHA-256', enc);
    const bytes = new Uint8Array(digest);
    let hex = '';
    for (const b of bytes) hex += b.toString(16).padStart(2, '0');
    return hex;
}

// Sync derivation that uses the FNV fallback only when SubtleCrypto is
// not available *and* a synchronous answer is required. Prefer the
// async deriveUserId for production use.
export function deriveUserIdSync(role, profileId, salt) {
    if (!role || !profileId) return null;
    const prefix = ROLE_PREFIXES[role];
    if (!prefix) {
        throw new Error(`[analytics] Unknown role for user_id derivation: ${role}`);
    }
    return fnv1a64(`${prefix}${profileId}${salt || ''}`);
}

export async function deriveUserId(role, profileId, salt) {
    if (!role || !profileId) return null;
    const prefix = ROLE_PREFIXES[role];
    if (!prefix) {
        throw new Error(`[analytics] Unknown role for user_id derivation: ${role}`);
    }
    const full = await sha256Hex(`${prefix}${profileId}${salt || ''}`);
    return full.slice(0, 16);
}
